/* eslint-disable no-mixed-spaces-and-tabs */
import { get, post } from '@/util/request.js';
import { LRUCache } from 'lru-cache';
import { getEntityclassByExternalId } from './eav.js';
import { Search, EAV } from '@fibl/zdb-common';
import { Constants } from '@fibl/oxs-common';

export type Pagination = { start?: number; limit?: number };
export const searchWindow = Constants.SEARCH_WINDOW;

export type SearchFilter = Search.SearchFilter;

async function coalesceEc(ec: string): Promise<string> {
	if (ec.length < 35) {
		let ecr = await getEntityclassByExternalId(ec);
		return ecr.id;
	}
	return ec;
}

export async function findAll<T extends Record<string, any>>(
	entityclass: string,
	filter?: any,
	allowCache?: boolean,
): Promise<EAV.Entity<T>[]> {
	let result: EAV.Entity<any>[] = [];
	const searchRequest: Record<string, any> = {
		pagination: {
			start: 0,
			limit: searchWindow,
		},
	};
	searchRequest.entityClass = await coalesceEc(entityclass);
	if (filter) searchRequest.attributeFilter = filter;
	let temp;
	do {
		temp = !allowCache
			? await post('search/search/execute', searchRequest)
			: await get('search/search/execute', searchRequest, undefined, undefined, true);
		result = result.concat(temp);
		searchRequest.pagination.start += searchRequest.pagination.limit;
	} while (temp.length >= searchWindow);
	return result;
}

export async function search<T extends Record<string, any>, U extends Record<string, any> = {}>(
	entityclass: string,
	filter?: SearchFilter,
	pagination?: Pagination,
	sorting?: string | string[],
): Promise<EAV.Entity<T, U>[]> {
	if (!entityclass) throw new Error('missing entityclass for search');
	let params: any = { entityClass: await coalesceEc(entityclass) };
	if (filter) params.attributeFilter = filter;
	if (pagination) params.pagination = pagination;
	if (sorting) params.sorting = sorting;
	return post('search/search/execute', params);
}

export async function searchOnlyIds(
	entityclass: string,
	filter?: SearchFilter,
	pagination?: Pagination,
	sorting?: string | string[],
): Promise<string[]> {
	if (!entityclass) throw new Error('missing entityclass for search');
	let params: any = { entityClass: await coalesceEc(entityclass), onlyIds: true };
	if (filter) params.attributeFilter = filter;
	if (pagination) params.pagination = pagination;
	if (sorting) params.sorting = sorting;
	return post('search/search/execute', params);
}

export async function countEntries(entityClass: string, filter?: SearchFilter): Promise<number> {
	const endpoint = 'search/search/count';
	const searchRequest: Record<string, any> = {
		entityClass: await coalesceEc(entityClass),
	};
	if (filter) searchRequest.attributeFilter = filter;
	const result = await post(endpoint, searchRequest);
	return result;
}

export async function exists(entityClass: string, attributeFilter: SearchFilter): Promise<boolean> {
	const searchRequest = {
		entityClass: await coalesceEc(entityClass),
		attributeFilter,
		pagination: {
			start: 0,
			limit: 1,
		},
	};
	const result = await post('search/search/execute', searchRequest);
	return !!result.length;
}

const entitycache = new LRUCache<string, any>({
	max: 500,
	ttl: 1000 * 60 * 60,
});

export function getEntityByIdRev<T extends Record<string, any>>(
	id: string,
	rev: number,
): Promise<EAV.Entity<T>> {
	const key = `${id}_${rev}`;
	let p = entitycache.get(key);
	if (!p) {
		p = get(`eav/entity/link/${id}/${rev}`, {});
		entitycache.set(key, p);
	}
	return p;
}

export async function getEntityById<T extends Record<string, any>>(
	id: string,
): Promise<EAV.Entity<T>> {
	const list = await getEntitiesById([id]);
	if (list.length === 0) throw new Error(`entity not found: ${id}`); // never possible?! so no translation
	return list[0];
}

export function getEntitiesById(ids: string[]): Promise<EAV.Entity<any>[]> {
	return post('search/search/ids', { ids });
}

export async function getEntityByAlias<T extends Record<string, any>>(
	alias: string,
	ec: string,
): Promise<EAV.Entity<T>> {
	const list = await search<T>(
		ec,
		{
			field: '/alias',
			comparison: 'eq',
			value: alias,
		},
		{ limit: 1 },
	);
	return list[0];
}

export async function getEntityIdByAlias(alias: string, ec: string): Promise<string> {
	const list = await searchOnlyIds(
		ec,
		{
			field: '/alias',
			comparison: 'eq',
			value: alias,
		},
		{ limit: 1 },
	);
	return list[0];
}

export type AggregateRunPayload = {
	entityClass: string;
	attributeFilter?: SearchFilter;
} & ({ aggregate: Search.AggregateLayer } | { aggregates: Search.AggregateLayer[] });

export async function aggregateRun(payload: AggregateRunPayload) {
	payload.entityClass = await coalesceEc(payload.entityClass);
	return post('search/aggregate/run', payload);
}



export type UpdateSearchListPayload = {
	entityClass: string;
	sorting?: string[] | string;
	filter?: UpdateSearchListFilter;
	pagination?: {
		start: number;
		limit: number;
	};
};
export type UpdateSearchListFilter = {
	before?: string;
	after?: string;
	account?: string;
	paths?: string[];
	ops?: OpsArray[];
};
type OpsArray = 'add' | 'replace' | 'remove';

export type UpdateSearchListResponse = {
	entityclass: string;
	entity: string;
	rev: number;
	updated: string;
	paths: UpdateSearchPathResponse;
	patch?: EAV.JsonPatch;
	account: string | null;
};
type UpdateSearchPathResponse = {
	add: UpdateSearchPath[];
	replace: UpdateSearchPath[];
	remove: UpdateSearchPath[];
};
type UpdateSearchPath = {
	key: string;
	type: string;
};
export async function updatesList(
	payload: UpdateSearchListPayload,
): Promise<UpdateSearchListResponse[]> {
	payload.entityClass = await coalesceEc(payload.entityClass);
	return post('search/updatesearch/list',
		payload,
	);
}

export type UpdateSearchPayload = {
	entityClass: string;
	filter: UpdateSearchListFilter;
};
export async function updatesCount(payload: UpdateSearchPayload): Promise<number> {
	payload.entityClass = await coalesceEc(payload.entityClass);
	return post('search/updatesearch/count',
		payload,
	);
}